import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { trpcClient } from "@/lib/trpc";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Loader2, History, ArrowRight } from "lucide-react";
import { formatDate } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";

interface VersionHistoryModalProps {
  productId: string;
  currentVersion: number;
  trigger: React.ReactNode;
}

export function VersionHistoryModal({
  productId,
  currentVersion,
  trigger,
}: VersionHistoryModalProps) {
  const [open, setOpen] = useState(false);

  const { data: history, isLoading } = useQuery({
    queryKey: ["product-versions", productId],
    queryFn: async () =>
      trpcClient.products.getVersionHistory.query({ id: productId }),
    enabled: open,
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-[700px] max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="h-5 w-5 text-indigo-500" />
            Lịch sử phiên bản
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="flex-1 pr-4 mt-2">
          {isLoading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-indigo-500" />
            </div>
          ) : history && history.length > 0 ? (
            <div className="space-y-6 relative before:absolute before:inset-0 before:ml-5 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-slate-200 before:to-transparent">
              {history.map((log: any, index: number) => {
                const isCurrent = log.version === currentVersion;
                const changes = log.changeSummary
                  ? JSON.parse(log.changeSummary)
                  : null;

                return (
                  <div
                    key={log.id}
                    className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active"
                  >
                    <div className="flex items-center justify-center w-10 h-10 rounded-full border border-white bg-slate-100 text-slate-500 shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10">
                      <span className="text-xs font-semibold">
                        v{log.version}
                      </span>
                    </div>
                    <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] bg-white p-4 rounded-lg border border-slate-200 shadow-sm">
                      <div className="flex flex-col gap-1 mb-2">
                        <div className="flex items-center justify-between">
                          <Badge
                            variant={isCurrent ? "default" : "outline"}
                            className={
                              isCurrent
                                ? "bg-indigo-500 text-white"
                                : "text-slate-500"
                            }
                          >
                            {isCurrent
                              ? "Phiên bản hiện tại"
                              : `Phiên bản v${log.version}`}
                          </Badge>
                          <span className="text-xs text-slate-500 font-medium">
                            {formatDate(log.createdAt, "long")}
                          </span>
                        </div>
                        <div className="text-xs text-slate-600 font-medium mt-1">
                          Cập nhật bởi: {log.createdBy?.fullName || "Hệ thống"}
                        </div>
                      </div>

                      {changes &&
                      changes.changes &&
                      changes.changes.length > 0 ? (
                        <div className="mt-3 text-sm text-slate-600 bg-slate-50 rounded-md p-3 border border-slate-100">
                          <p className="font-semibold text-xs text-slate-700 mb-2">
                            Chi tiết thay đổi:
                          </p>
                          <ul className="space-y-1.5 list-disc list-inside">
                            {changes.changes.map((change: any, i: number) => (
                              <li key={i} className="text-xs leading-relaxed">
                                <span className="font-medium text-slate-700">
                                  {change.field}:
                                </span>{" "}
                                <span className="text-rose-500 line-through mr-1">
                                  {change.old || "Trống"}
                                </span>
                                <ArrowRight className="inline-block w-3 h-3 text-slate-400 mx-1" />
                                <span className="text-emerald-600">
                                  {change.new || "Trống"}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ) : (
                        <div className="mt-2 text-xs text-slate-500 italic">
                          Không có chi tiết thay đổi cụ thể hoặc tạo mới.
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-10 text-slate-500">
              Không tìm thấy lịch sử thay đổi nào.
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
